import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DashboardLayout from "@/components/layout/DashboardLayout";
import {
  Lightbulb,
  Sparkles,
  Loader2,
  Calendar,
  ArrowRight,
  ArrowLeft,
  TrendingUp,
  Target,
  Eye,
  Bookmark,
  BookmarkCheck,
  Copy,
  Check,
  Globe,
  Users,
  Clock,
  Heart,
  Share2,
  Zap,
  Search,
  Filter,
  RefreshCw,
  Briefcase,
  Building,
  Home,
  Car,
  Baby,
  Dog,
  Palette,
  Music,
  Camera,
  Gamepad,
  Coffee,
  Shirt,
  Leaf,
  Church,
  Star,
  Utensils,
  Plane,
  Dumbbell,
  GraduationCap,
  ShoppingBag,
  Radar,
  ExternalLink
} from "lucide-react";

// Niche options for AI-generated ideas
const nicheOptions = [
  { id: "marketing", label: "Marketing", icon: Briefcase, color: "#FF6B35" },
  { id: "tech", label: "Technology", icon: Lightbulb, color: "#3B82F6" },
  { id: "health", label: "Health & Wellness", icon: Heart, color: "#EC4899" },
  { id: "finance", label: "Finance", icon: Building, color: "#10B981" },
  { id: "food", label: "Food & Recipes", icon: Utensils, color: "#F59E0B" },
  { id: "fashion", label: "Fashion & Style", icon: Shirt, color: "#8B5CF6" },
  { id: "travel", label: "Travel", icon: Plane, color: "#06B6D4" },
  { id: "education", label: "Education", icon: GraduationCap, color: "#6366F1" },
  { id: "fitness", label: "Fitness", icon: Dumbbell, color: "#EF4444" },
  { id: "ecommerce", label: "E-commerce", icon: ShoppingBag, color: "#14B8A6" },
  { id: "beauty", label: "Beauty & Skincare", icon: Sparkles, color: "#F472B6" },
  { id: "realestate", label: "Real Estate", icon: Home, color: "#84CC16" },
  { id: "automotive", label: "Automotive", icon: Car, color: "#64748B" },
  { id: "parenting", label: "Parenting", icon: Baby, color: "#FB923C" },
  { id: "pets", label: "Pets & Animals", icon: Dog, color: "#A78BFA" },
  { id: "art", label: "Art & Design", icon: Palette, color: "#E879F9" },
  { id: "music", label: "Music", icon: Music, color: "#22D3EE" },
  { id: "photography", label: "Photography", icon: Camera, color: "#FCD34D" },
  { id: "gaming", label: "Gaming", icon: Gamepad, color: "#4ADE80" },
  { id: "lifestyle", label: "Lifestyle", icon: Coffee, color: "#C084FC" },
  { id: "sustainability", label: "Sustainability", icon: Leaf, color: "#22C55E" },
  { id: "spiritual", label: "Spiritual & Religious", icon: Church, color: "#FBBF24" },
  { id: "entertainment", label: "Entertainment", icon: Star, color: "#F87171" },
  { id: "sports", label: "Sports", icon: Dumbbell, color: "#38BDF8" }
];

export default function ContentIdeasPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Main tab state
  const [activeTab, setActiveTab] = useState("trending"); // trending, calendar, prompts, saved, radar
  
  // AI Prompts state
  const [selectedNiches, setSelectedNiches] = useState([]);
  const [generatedPrompts, setGeneratedPrompts] = useState([]);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  
  // Saved ideas state
  const [savedIdeas, setSavedIdeas] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  
  // Trending content state
  const [trendingTopics, setTrendingTopics] = useState([]);
  const [loadingTrending, setLoadingTrending] = useState(false);
  
  // Content Calendar state
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  
  // Competitor tracking (from Content Radar)
  const [competitors, setCompetitors] = useState([]);
  const [competitorPosts, setCompetitorPosts] = useState([]);
  const [newCompetitorUrl, setNewCompetitorUrl] = useState("");
  const [loadingCompetitor, setLoadingCompetitor] = useState(false);
  
  // Load saved ideas from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("viraly_saved_ideas");
    if (saved) {
      setSavedIdeas(JSON.parse(saved));
    }
    
    // Load competitors
    fetchCompetitors();
    // Load calendar events
    fetchCalendarEvents();
    // Load trending topics
    fetchTrendingTopics();
  }, []);
  
  const fetchCalendarEvents = async () => {
    setLoadingCalendar(true);
    try {
      const res = await apiClient.get("/content/calendar-events");
      setCalendarEvents(res.data.events || []);
    } catch (err) {
      console.log("Calendar events not available, using defaults");
      // Fallback to static events
      setCalendarEvents([
        { date: "Apr 1", event: "April Fools' Day", emoji: "🃏", category: "fun" },
        { date: "Apr 7", event: "World Health Day", emoji: "🏥", category: "health" },
        { date: "Apr 22", event: "Earth Day", emoji: "🌍", category: "sustainability" },
      ]);
    } finally {
      setLoadingCalendar(false);
    }
  };
  
  const fetchTrendingTopics = async () => {
    setLoadingTrending(true);
    try {
      const res = await apiClient.get("/content/trending-topics");
      setTrendingTopics(res.data.topics || []);
    } catch (err) {
      console.log("Trending topics not available, using defaults");
      // Fallback to static topics
      setTrendingTopics([
        { topic: "#AIContent", platform: "All", engagement: "High" },
        { topic: "Short-form Video", platform: "TikTok", engagement: "Very High" },
        { topic: "Behind the Scenes", platform: "Instagram", engagement: "High" },
      ]);
    } finally {
      setLoadingTrending(false);
    }
  };
  
  const fetchCompetitors = async () => {
    try {
      const res = await apiClient.get("/content/radar/competitors");
      setCompetitors(res.data.competitors || []);
    } catch (err) {
      console.log("Content Radar not available");
    }
  };
  
  const toggleNiche = (nicheId) => {
    setSelectedNiches(prev => {
      if (prev.includes(nicheId)) {
        return prev.filter(n => n !== nicheId);
      }
      if (prev.length >= 3) {
        toast.info("Select up to 3 niches");
        return prev;
      }
      return [...prev, nicheId];
    });
  };
  
  const generatePrompts = async () => {
    if (selectedNiches.length === 0) {
      toast.error("Please select at least one niche");
      return;
    }
    
    setLoadingPrompts(true);
    try {
      const niches = selectedNiches.map(id => nicheOptions.find(n => n.id === id)?.label).join(", ");
      
      const res = await apiClient.post("/content/generate-prompts", {
        niches: niches,
        count: 10
      });
      
      setGeneratedPrompts(res.data.prompts || []);
      toast.success("Generated 10 creative prompts!");
    } catch (err) {
      // Fallback: Generate prompts client-side if API not available
      const niches = selectedNiches.map(id => nicheOptions.find(n => n.id === id)?.label);
      const fallbackPrompts = [
        `Share 3 common myths about ${niches[0]} that most people believe`,
        `The unexpected connection between ${niches[0]} and daily productivity`,
        `5 things I wish I knew before starting in ${niches[0]}`,
        `Hot take: Why most ${niches[0]} advice is actually wrong`,
        `Behind the scenes of a day in the ${niches[0]} industry`,
        `The future of ${niches[0]}: What's coming in 2026`,
        `Beginner's mistake #1 in ${niches[0]} (and how to avoid it)`,
        `This ${niches[0]} trend is changing everything`,
        `Unpopular opinion about ${niches[0]} that needs to be said`,
        `How to get started in ${niches[0]} with zero budget`
      ];
      setGeneratedPrompts(fallbackPrompts);
      toast.success("Generated 10 creative prompts!");
    } finally {
      setLoadingPrompts(false);
    }
  };
  
  const saveIdea = (idea) => {
    const newIdea = {
      id: Date.now(),
      text: idea,
      savedAt: new Date().toISOString(),
      niches: selectedNiches
    };
    const updated = [...savedIdeas, newIdea];
    setSavedIdeas(updated);
    localStorage.setItem("viraly_saved_ideas", JSON.stringify(updated));
    toast.success("Idea saved!");
  };
  
  const removeIdea = (ideaId) => {
    const updated = savedIdeas.filter(i => i.id !== ideaId);
    setSavedIdeas(updated);
    localStorage.setItem("viraly_saved_ideas", JSON.stringify(updated));
    toast.success("Idea removed");
  };
  
  const copyIdea = (idea, id) => {
    navigator.clipboard.writeText(idea);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success("Copied to clipboard!");
  };
  
  const createFromIdea = (ideaText) => {
    // Check if user has credits and show warning if not
    const postsRemaining = user?.posts_remaining || 0;
    if (postsRemaining <= 0) {
      toast.warning("You'll need to buy credits to generate this post", {
        action: {
          label: "Buy Now",
          onClick: () => navigate("/pricing?tab=payg&item=posts")
        },
        duration: 4000
      });
    }
    // Navigate to Create+ with the idea pre-filled (user can still edit)
    navigate("/create", { state: { prefillText: ideaText, fromIdeas: true } });
  };
  
  const addCompetitor = async () => {
    if (!newCompetitorUrl.trim()) return;
    
    setLoadingCompetitor(true);
    try {
      await apiClient.post("/content/radar/competitors", {
        url: newCompetitorUrl
      });
      toast.success("Competitor added!");
      setNewCompetitorUrl("");
      fetchCompetitors();
    } catch (err) {
      toast.error("Could not analyze that profile");
    } finally {
      setLoadingCompetitor(false);
    }
  };
  
  const tabs = [
    { id: "prompts", label: "AI Prompts", icon: Sparkles, color: "text-viraly-orange" },
    { id: "calendar", label: "Content Calendar", icon: Calendar, color: "text-blue-400" },
    { id: "trending", label: "Trending Topics", icon: TrendingUp, color: "text-green-400" },
    { id: "radar", label: "Competitor Radar", icon: Radar, color: "text-purple-400" },
    { id: "saved", label: "Saved Ideas", icon: Bookmark, color: "text-yellow-400" },
  ];

  return (
    <DashboardLayout title="Content Inspiration Hub">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-viraly-light flex items-center gap-3">
              <Lightbulb className="w-7 h-7 text-viraly-orange" />
              Inspiration Hub
            </h1>
            <p className="text-sm text-viraly-muted mt-1">
              Discover content ideas, track competitors, and never run out of things to post
            </p>
          </div>
          <Button 
            onClick={() => navigate("/create")}
            className="bg-viraly-orange hover:bg-viraly-orange/80"
          >
            <Zap className="w-4 h-4 mr-2" />
            Create Content
          </Button>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-white/10 pb-3">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeTab === tab.id
                  ? "bg-white/10 text-viraly-light"
                  : "text-viraly-muted hover:bg-white/5"
              }`}
            >
              <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? tab.color : ""}`} />
              <span className="text-sm font-medium">{tab.label}</span>
              {tab.id === "saved" && savedIdeas.length > 0 && (
                <span className="px-1.5 py-0.5 text-[10px] bg-yellow-500/20 text-yellow-400 rounded-full">
                  {savedIdeas.length}
                </span>
              )}
            </button>
          ))}
        </div>
        
        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {/* AI PROMPTS TAB */}
          {activeTab === "prompts" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              <div className="card-base p-6">
                <h2 className="font-heading font-bold text-lg mb-2">
                  <Sparkles className="w-5 h-5 inline mr-2 text-viraly-orange" />
                  Generate Content Prompts
                </h2>
                <p className="text-sm text-viraly-muted mb-4">
                  Select up to 3 niches and we'll generate 10 creative post ideas you can use
                </p>
                
                {/* Niche Selection Grid */}
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-6">
                  {nicheOptions.map(niche => {
                    const Icon = niche.icon;
                    const isSelected = selectedNiches.includes(niche.id);
                    return (
                      <button
                        key={niche.id}
                        onClick={() => toggleNiche(niche.id)}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          isSelected
                            ? "border-viraly-orange bg-viraly-orange/10"
                            : "border-white/10 hover:border-white/20"
                        }`}
                        style={isSelected ? { borderColor: niche.color, backgroundColor: `${niche.color}15` } : {}}
                      >
                        <Icon className="w-5 h-5 mx-auto mb-1" style={{ color: niche.color }} />
                        <p className="text-[10px] text-viraly-muted">{niche.label}</p>
                      </button>
                    );
                  })}
                </div>
                
                <div className="flex items-center gap-3">
                  <Button
                    onClick={generatePrompts}
                    disabled={loadingPrompts || selectedNiches.length === 0}
                    className="bg-viraly-orange hover:bg-viraly-orange/80"
                  >
                    {loadingPrompts ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    Generate 10 Ideas
                  </Button>
                  {selectedNiches.length > 0 && (
                    <p className="text-xs text-viraly-muted">
                      {selectedNiches.length}/3 niches selected
                    </p>
                  )}
                </div>
              </div>
              
              {/* Generated Prompts */}
              {generatedPrompts.length > 0 && (
                <div className="card-base p-6">
                  <h3 className="font-heading font-bold mb-4">Your Content Ideas</h3>
                  <div className="space-y-3">
                    {generatedPrompts.map((prompt, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all group">
                        <span className="w-6 h-6 rounded-full bg-viraly-orange/20 text-viraly-orange text-xs flex items-center justify-center font-bold">
                          {idx + 1}
                        </span>
                        <p className="flex-1 text-sm text-viraly-light">{prompt}</p>
                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => copyIdea(prompt, idx)}
                            className="p-1.5 rounded hover:bg-white/10"
                            title="Copy"
                          >
                            {copiedId === idx ? (
                              <Check className="w-4 h-4 text-green-400" />
                            ) : (
                              <Copy className="w-4 h-4 text-viraly-muted" />
                            )}
                          </button>
                          <button
                            onClick={() => saveIdea(prompt)}
                            className="p-1.5 rounded hover:bg-white/10"
                            title="Save for later"
                          >
                            <Bookmark className="w-4 h-4 text-viraly-muted" />
                          </button>
                          <button
                            onClick={() => createFromIdea(prompt)}
                            className="p-1.5 rounded bg-viraly-orange/20 hover:bg-viraly-orange/30"
                            title="Create this post"
                          >
                            <ArrowRight className="w-4 h-4 text-viraly-orange" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}
          
          {/* CONTENT CALENDAR TAB */}
          {activeTab === "calendar" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              <div className="card-base p-6">
                <h2 className="font-heading font-bold text-lg mb-2">
                  <Calendar className="w-5 h-5 inline mr-2 text-blue-400" />
                  Upcoming Events & Holidays
                </h2>
                <p className="text-sm text-viraly-muted mb-4">
                  Plan your content around these events (localized to your region)
                </p>
                
                {loadingCalendar ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-viraly-orange" />
                    <span className="ml-2 text-viraly-muted">Loading events...</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {calendarEvents.map((event, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all group"
                      >
                        <div className="text-3xl">{event.emoji}</div>
                        <div className="flex-1">
                          <p className="font-medium text-viraly-light">{event.event}</p>
                          <p className="text-xs text-viraly-muted">
                            {event.date}
                            {event.scope && event.scope !== "global" && (
                              <span className="ml-2 px-1.5 py-0.5 rounded bg-white/10 text-[10px]">
                                {event.scope}
                              </span>
                            )}
                          </p>
                        </div>
                        <button
                          onClick={() => createFromIdea(`Create a post for ${event.event}`)}
                          className="px-3 py-1.5 rounded-lg bg-blue-500/20 text-blue-400 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          Create Post
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {calendarEvents.length > 0 && (
                  <p className="text-xs text-viraly-muted mt-4 text-center">
                    Showing {calendarEvents.length} events • Based on your location
                  </p>
                )}
              </div>
            </motion.div>
          )}
          
          {/* TRENDING TOPICS TAB */}
          {activeTab === "trending" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              <div className="card-base p-6">
                <h2 className="font-heading font-bold text-lg mb-2">
                  <TrendingUp className="w-5 h-5 inline mr-2 text-green-400" />
                  Trending Topics
                </h2>
                <p className="text-sm text-viraly-muted mb-4">
                  Personalized trends based on your niche and brand
                </p>
                
                {loadingTrending ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-viraly-orange" />
                    <span className="ml-2 text-viraly-muted">Finding relevant trends...</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {trendingTopics.map((trend, idx) => (
                      <div 
                        key={idx}
                        className="p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all group"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-viraly-light">{trend.topic}</p>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <span className="text-[10px] px-2 py-0.5 rounded bg-white/10 text-viraly-muted">
                                {trend.platform}
                              </span>
                              <span className={`text-[10px] px-2 py-0.5 rounded ${
                                trend.engagement === "Very High" ? "bg-green-500/20 text-green-400" :
                                trend.engagement === "High" ? "bg-blue-500/20 text-blue-400" :
                                trend.engagement === "Personalized" ? "bg-viraly-orange/20 text-viraly-orange" :
                                "bg-white/10 text-viraly-muted"
                              }`}>
                                {trend.engagement}
                              </span>
                              {trend.category && (
                                <span className="text-[10px] px-2 py-0.5 rounded bg-viraly-orange/20 text-viraly-orange">
                                  {trend.category}
                                </span>
                              )}
                              {trend.brand_relevance === "very_high" && (
                                <span className="text-[10px] px-2 py-0.5 rounded bg-green-500/20 text-green-400 flex items-center gap-1">
                                  <Sparkles className="w-3 h-3" /> Your Brand
                                </span>
                              )}
                            </div>
                            {trend.suggestion && (
                              <p className="text-xs text-viraly-muted mt-2 italic">{trend.suggestion}</p>
                            )}
                          </div>
                          <button
                            onClick={() => createFromIdea(`Create content about: ${trend.topic}`)}
                            className="px-3 py-1.5 rounded-lg bg-green-500/20 text-green-400 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-3"
                            data-testid={`create-from-trend-${idx}`}
                          >
                            Create Post
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
          
          {/* COMPETITOR RADAR TAB */}
          {activeTab === "radar" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              <div className="card-base p-6">
                <h2 className="font-heading font-bold text-lg mb-2">
                  <Radar className="w-5 h-5 inline mr-2 text-purple-400" />
                  Competitor Radar
                </h2>
                <p className="text-sm text-viraly-muted mb-4">
                  Track what your competitors are posting and get inspired
                </p>
                
                {/* Add Competitor */}
                <div className="flex gap-3 mb-6">
                  <Input
                    placeholder="Enter competitor's social URL (Facebook, Instagram, etc.)"
                    value={newCompetitorUrl}
                    onChange={(e) => setNewCompetitorUrl(e.target.value)}
                    className="input-base flex-1"
                  />
                  <Button
                    onClick={addCompetitor}
                    disabled={loadingCompetitor || !newCompetitorUrl.trim()}
                    className="bg-purple-500 hover:bg-purple-600"
                  >
                    {loadingCompetitor ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Target className="w-4 h-4 mr-2" />
                    )}
                    Track
                  </Button>
                </div>
                
                {competitors.length > 0 ? (
                  <div className="space-y-4">
                    {competitors.map((comp, idx) => (
                      <div key={idx} className="p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                            <Target className="w-5 h-5 text-purple-400" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-viraly-light">{comp.name || "Tracked Profile"}</p>
                            <a 
                              href={comp.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-viraly-muted hover:text-viraly-orange flex items-center gap-1"
                            >
                              {comp.url?.substring(0, 40)}...
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                          <button
                            onClick={() => createFromIdea(`Create content inspired by ${comp.name || comp.url}: ${comp.analysis?.title || comp.analysis?.description || ''}`)}
                            className="px-3 py-1.5 rounded-lg bg-viraly-orange/20 text-viraly-orange text-xs font-medium"
                          >
                            Get Inspired
                          </button>
                        </div>
                        {comp.analysis && (
                          <div className="ml-13 pl-3 border-l border-white/10">
                            {comp.analysis.title && (
                              <p className="text-sm text-viraly-light">{comp.analysis.title}</p>
                            )}
                            {comp.analysis.description && (
                              <p className="text-xs text-viraly-muted mt-1">{comp.analysis.description}</p>
                            )}
                            {comp.analysis.scraped_at && (
                              <p className="text-[10px] text-viraly-muted/50 mt-2">
                                Last analyzed: {new Date(comp.analysis.scraped_at).toLocaleDateString()}
                              </p>
                            )}
                            {comp.analysis.error && (
                              <p className="text-xs text-red-400 mt-1">{comp.analysis.error}</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-viraly-muted">
                    <Target className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No competitors tracked yet</p>
                    <p className="text-xs mt-1">Add a competitor URL above to start tracking</p>
                  </div>
                )}
                
                <div className="mt-4 p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                  <p className="text-xs text-purple-400">
                    <strong>Pro Tip:</strong> Visit the full{" "}
                    <button 
                      onClick={() => navigate("/radar")}
                      className="underline hover:no-underline"
                    >
                      Content Radar
                    </button>
                    {" "}for advanced competitor tracking and analysis
                  </p>
                </div>
              </div>
            </motion.div>
          )}
          
          {/* SAVED IDEAS TAB */}
          {activeTab === "saved" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              <div className="card-base p-6">
                <h2 className="font-heading font-bold text-lg mb-2">
                  <BookmarkCheck className="w-5 h-5 inline mr-2 text-yellow-400" />
                  Saved Ideas ({savedIdeas.length})
                </h2>
                <p className="text-sm text-viraly-muted mb-4">
                  Your saved content ideas ready to create
                </p>
                
                {savedIdeas.length > 0 ? (
                  <div className="space-y-3">
                    {savedIdeas.map((idea) => (
                      <div key={idea.id} className="flex items-start gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all group">
                        <BookmarkCheck className="w-5 h-5 text-yellow-400 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-viraly-light">{idea.text}</p>
                          <p className="text-[10px] text-viraly-muted mt-1">
                            Saved {new Date(idea.savedAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => removeIdea(idea.id)}
                            className="p-1.5 rounded hover:bg-red-500/20"
                            title="Remove"
                          >
                            <span className="text-red-400 text-xs">✕</span>
                          </button>
                          <button
                            onClick={() => createFromIdea(idea.text)}
                            className="px-3 py-1.5 rounded-lg bg-viraly-orange/20 hover:bg-viraly-orange/30 text-viraly-orange text-xs font-medium"
                          >
                            Create →
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-viraly-muted">
                    <Bookmark className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No saved ideas yet</p>
                    <p className="text-xs mt-1">Generate prompts and save the ones you like</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
}
